import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Box, Grid, Stack, Typography, Card, IconButton, List, ListItem, CircularProgress, TextField, InputAdornment } from '@mui/material';
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { socket } from 'socket';
import { AgGridReact } from "ag-grid-react";
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-quartz.css';
// icons
import VisibilityIcon from '@mui/icons-material/Visibility';
import PlusIcon from "common/customIcons/plus";
import SearchIcon from "common/customIcons/search";
import AngleRightIcon from "common/customIcons/angle_right";
import DeleteIcon from "common/customIcons/delete";
// utils and helpers
import { debounce } from "utilities/common";
import { getDistributionConfig, getDriverKitProducts, getProductScan, holdDriverKit, keepExtraDriverKitProduct, resetDriverKit, resetDriverKitProduct } from "../../../../redux/slices/hyperwolf/inventoryDistribution";
import { closeCustomModalDialog, openCustomModalDialog, setModalLoader } from "../../../../redux/slices/modalSlice";
// custom components
import CommonLayout from "layouts/DashboardLayout";
import MDButton from "components/MDButton";
import { BrandFilter, BoxFilter } from "common/filters";
import BarcodeScanArea from 'components/BarcodeScanArea';
import SocketMobileScanner from 'components/SocketMobileScanner';
import { showToastMsg } from "components/distributionSetting/distributionManagement/addRegionKit";
import NoDataFound from "common/component/NoDataFound";
import { defaultColDef } from "components/tables/CommonAgGridTable";
import useSocket from 'HOC/useSocket';

const DriverKitDetails = () => {
  const { isConnected } = useSocket()

  const dispatch = useDispatch()
  const navigate = useNavigate()
  const skipHoldOnUnmountRef = useRef(false);
  const statusRef = useRef("");
  const { distributionId, driverId } = useParams()
  const [searchParams] = useSearchParams()
  const { regionId, subRegionId } = Object.fromEntries(searchParams.entries())

  const { user } = useSelector(state => state.auth || {})
  const { isOpen } = useSelector(state => state.modal || {})

  const [details, setDetails] = useState({});
  const [customFilters, setCustomFilters] = useState({});
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [distributionConfigs, setDistributionConfigs] = useState({});
  const [resettingProduct, setResettingProduct] = useState("");
  const [selectedProductId, setSelectedProductId] = useState("");
  const [isScanning, setIsScanning] = useState(false);

  const getRowId = useCallback((params) => params.data.productId, []);

  const columnDefs = useMemo(() => {
    const columns = [
      {
        headerName: 'Product Name/SKU',
        field: 'name',
        suppressMenu: false,
        flex: 2,
        minWidth: 240,
        sortable: false,
        cellRenderer: (params) => {
          if (!params?.data) return "-";
          const { productName, sku, assets } = params?.data || {}
          const { thumbURL, name } = assets || {}

          return <Box style={{ display: "flex", gap: "10px" }}>
            <img src={thumbURL} alt={name} width={30} height={30} />
            <Box>
              <Typography variant='body2' style={{ color: "#2E2E3A", fontWeight: "500", fontSize: "12px", padding: "0" }}>{productName}</Typography>
              {sku && <Typography variant='body2' style={{ color: "#9A9AAF", fontSize: "12px", padding: "0" }}>{sku}</Typography>}
            </Box>
          </Box>
        }
      },
      {
        headerName: 'Brand',
        field: 'brandName',
        suppressMenu: false,
        flex: 1.5,
        minWidth: 180,
        sortable: false,
        valueGetter: ({ data }) => data?.brand?.name || "-"
      },
      {
        headerName: 'Weight',
        field: 'weight',
        suppressMenu: false,
        flex: 1,
        minWidth: 120,
        sortable: false,
        valueGetter: ({ data }) => data?.weight ? `${data?.weight}g` : "-"
      },
      // {
      //   headerName: 'Batch',
      //   field: 'batch',
      //   suppressMenu: false,
      //   flex: 1.25,
      //   minWidth: 160,
      //   sortable: false,
      //   cellRenderer: ({ data }) => {
      //     const batches = data?.productBatches || [];
      //     const firstFive = batches.slice(0, 2);
      //     const rest = batches.slice(2);

      //     return (
      //       <Box>
      //         {firstFive.map((batch, idx) => (
      //           <Typography
      //             key={batch?.batchNo + idx}
      //             variant='body2'
      //             style={{ color: "#2E2E3A", fontWeight: "500", fontSize: "12px", padding: "0", lineHeight: "normal" }}
      //           >
      //             {batch?.sku} (Qty {batch?.assignedQty})
      //           </Typography>
      //         ))}
      //         {rest.length > 0 && (
      //           <Tooltip
      //             title={
      //               <Box>
      //                 {rest.map((batch, idx) => (
      //                   <Typography
      //                     key={batch?.batchNo + idx}
      //                     variant='body2'
      //                     style={{ color: "#ffffff", fontWeight: "500", fontSize: "12px", padding: "0", lineHeight: "normal" }}
      //                   >
      //                     {batch?.sku} (Qty {batch?.assignedQty})
      //                   </Typography>
      //                 ))}
      //               </Box>
      //             }
      //             placement="top"
      //             arrow
      //           >
      //             <Typography
      //               variant='body2'
      //               style={{
      //                 color: "#2E2E3A",
      //                 fontWeight: "500",
      //                 fontSize: "12px",
      //                 padding: "0",
      //                 cursor: "pointer",
      //                 textDecoration: "underline",
      //                 lineHeight: "normal"
      //               }}
      //             >
      //               +{rest.length} more
      //             </Typography>
      //           </Tooltip>
      //         )}
      //       </Box>
      //     );
      //   }
      // },
      {
        headerName: 'Expected Qty',
        field: 'expectedQty',
        suppressMenu: false,
        flex: 1,
        minWidth: 160,
        sortable: false,
      },
      {
        headerName: 'Scanned Qty',
        field: 'scannedQty',
        suppressMenu: false,
        flex: 1,
        minWidth: 160,
        sortable: false,
        cellRenderer: ({ data = {} }) => {
          const { scannedQty = 0, assignedQty = 0 } = data || {};
          let greenPercentage = 0;
          let redPercentage = 0;

          if (assignedQty > scannedQty) {
            greenPercentage = (scannedQty / assignedQty) * 100;
            redPercentage = 0;
          } else if (scannedQty > assignedQty) {
            greenPercentage = (assignedQty / scannedQty) * 100;
            redPercentage = 100 - greenPercentage;
          } else {
            greenPercentage = 100;
            redPercentage = 0;
          }

          return (
            <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
              <Box
                display="flex"
                flex={1}
                height={8}
                borderRadius={4}
                bgcolor="#e0e0e0"
                overflow="hidden"
                mr={1}
                justifyContent="space-between"
              >
                <Box
                  height="100%"
                  width={`${greenPercentage}%`}
                  bgcolor="#4caf50"
                />
                <Box
                  height="100%"
                  width={`${redPercentage}%`}
                  bgcolor="#f44336"
                />
              </Box>
              <Box fontSize="0.8rem" color="#555">
                {scannedQty}/{assignedQty}
              </Box>
            </Box>
          );
        },
      },
    ];

    // Append Actions column if status is not dispatched
    if (details?.status?.toLowerCase() !== "dispatched") {
      columns.push({
        headerName: 'Actions',
        field: 'actions',
        suppressMenu: false,
        flex: 1.8,
        sortable: false,
        cellStyle: { textAlign: 'right', justifyContent: 'end' },
        headerClass: 'right-aligned-cell-header',
        minWidth: 240,
        cellRenderer: ({ data = {} }) => {
          const { discrepancyQty, scannedQty, isNewProduct } = data || {};
          const scanned = data?.scannedQty || 0;
          const assigned = data?.assignedQty || 0;


          return <Box display="flex" alignItems="end" flexDirection="row" justifyContent="end" gap={1.5}>
            {
              data?.discrepancy ?
                <Box display="flex" flexDirection="column" gap={0.5}>
                  <Box display="flex" alignItems="center" gap={0.5} className="text-discrepancy">
                    <svg xmlns="http://www.w3.org/2000/svg" width="19" height="18" viewBox="0 0 19 18" fill="none">
                      <path d="M7.88912 3.12793C9.04623 2.89781 10.2454 3.0163 11.3354 3.46777C12.4255 3.91928 13.3576 4.68403 14.0131 5.66504C14.6684 6.64587 15.018 7.79896 15.018 8.97852C15.0162 10.5601 14.3874 12.077 13.269 13.1953C12.1507 14.3136 10.6337 14.9425 9.0522 14.9443C7.87252 14.9442 6.71961 14.5939 5.73873 13.9385C4.75774 13.283 3.99298 12.3517 3.54146 11.2617C3.09001 10.1718 2.97157 8.97247 3.20162 7.81543C3.43179 6.65829 4.00021 5.59501 4.83443 4.76074C5.66871 3.92646 6.73193 3.35811 7.88912 3.12793ZM9.0522 11.1484C8.89156 11.1485 8.73463 11.1959 8.60103 11.2852C8.46726 11.3745 8.3628 11.5017 8.30123 11.6504C8.2397 11.799 8.22395 11.9624 8.25533 12.1201C8.28671 12.2779 8.36327 12.4233 8.47701 12.5371C8.59078 12.6509 8.7362 12.7284 8.894 12.7598C9.05171 12.7911 9.21516 12.7744 9.36373 12.7129C9.51237 12.6513 9.63958 12.5469 9.72896 12.4131C9.81818 12.2795 9.8656 12.1226 9.86568 11.9619C9.86568 11.7464 9.78065 11.5392 9.62838 11.3867C9.47582 11.2342 9.26795 11.1484 9.0522 11.1484ZM9.0522 5.18262C8.90855 5.18275 8.77098 5.24021 8.66939 5.3418C8.56782 5.44342 8.51029 5.58093 8.51021 5.72461V9.52148C8.51031 9.66515 8.56781 9.80269 8.66939 9.9043C8.77098 10.0059 8.90855 10.0633 9.0522 10.0635C9.19604 10.0635 9.33429 10.006 9.43599 9.9043C9.53757 9.80269 9.59507 9.66515 9.59517 9.52148V5.72461C9.59509 5.58093 9.53757 5.44342 9.43599 5.3418C9.33429 5.24009 9.19604 5.18262 9.0522 5.18262ZM9.40181 1.9375C11.1443 2.02383 12.798 2.75377 14.0376 3.99316C15.1511 5.10676 15.8514 6.55532 16.0454 8.10156C15.8501 6.55608 15.1487 5.10926 14.0356 3.99609C12.7964 2.7569 11.1438 2.02556 9.40181 1.9375Z" fill="#FD4438" />
                    </svg>
                    <Typography color="#FD4438" fontWeight="600" style={{ fontSize: "12px" }}>{data?.discrepancyType}</Typography>
                  </Box>
                  <MDButton
                    size='small'
                    startIcon={<VisibilityIcon fontSize='small' width='14' height='14' />} variant='contained' fontWeight="500" color='primary' className='btn-primary-light'
                    style={{ fontSize: "12px", minHeight: "auto" }}
                    disabled={details?.status === "On Hold"}
                    onClick={() => {
                      const { isNewProduct, discrepancyId, productId } = data || {};
                      dispatch(openCustomModalDialog({
                        modalData: { distributionId, driverId, productId, discrepancyId, handleDriverProducts, isNewProduct },
                        modal: "ADD_DISCREPANCY_NOTE"
                      }))
                    }}
                  >
                    View Discrepancy {data?.discrepancyQty ? `(${data?.discrepancyQty})` : null}
                  </MDButton>
                </Box>
                : assigned > scanned
                  ? <MDButton
                    size='small'
                    startIcon={<PlusIcon fontSize='small' width='14' height='14' />} variant='contained' fontWeight="500" color='primary' className='btn-primary-light'
                    style={{ fontSize: "12px", minHeight: "auto" }}
                    disabled={details?.status === "On Hold"}
                    onClick={() => {
                      dispatch(openCustomModalDialog({
                        modalData: { distributionId, driverId, productId: data?.productId, handleDriverProducts },
                        modal: "ADD_DISCREPANCY_NOTE"
                      }))
                    }}
                  >
                    Add Discrepancy
                  </MDButton>
                  : null
            }
            {((discrepancyQty || scannedQty) && !isNewProduct) ?
              <MDButton
                size='small'
                variant='contained' fontWeight="500" color='#ffffff' className='btn-primary-light'
                style={{ fontSize: "12px", minHeight: "auto", color: "#ffffff", background: "#FD4438", boxShadow: "none !important" }}
                onClick={() => {
                  setSelectedProductId(data?.productId);
                  dispatch(openCustomModalDialog({
                    modalData: {
                      handleSubmit: handleResetProduct,
                      title: "Reset Product",
                      description: <>
                        This will reset all the scanned quantities and discrepancies for this product.
                        <br />
                        Are you sure you want to reset?
                      </>,
                      submitBtnLabel: "Reset",
                      productId: data?.productId,
                      actionType: "reset"
                    },
                    modal: 'CONFIRMATION_MODAL'
                  }))
                }}
                disabled={resettingProduct === data?.productId}
              >
                {resettingProduct === data?.productId ? <CircularProgress size={20} color="light" /> : "Reset"}
              </MDButton>
              : null
            }
            {isNewProduct && <>
              {/* Keep extra product */}
              <MDButton
                size='small'
                variant='contained' fontWeight="500" color='primary' className='btn-primary-light'
                style={{ fontSize: "12px", minHeight: "auto" }}
                onClick={() => {
                  setSelectedProductId(data?.productId);
                  dispatch(openCustomModalDialog({
                    modalData: {
                      handleSubmit: handleKeepExtraProduct,
                      title: "Keep Product",
                      description: <>
                        This will add the product in the driver kit.
                        <br />
                        Are you sure you want to add?
                      </>,
                      submitBtnLabel: "Add",
                      productId: data?.productId
                    },
                    modal: 'CONFIRMATION_MODAL'
                  }))
                }}
                disabled={resettingProduct === data?.productId}
              >
                {resettingProduct === data?.productId ? <CircularProgress size={20} color="light" /> : "Keep"}
              </MDButton>

              {/* Delete button */}
              <IconButton
                color='error'
                size='small'
                variant='primary'
                style={{ width: "32px", height: "32px" }}
                onClick={() => {
                  dispatch(openCustomModalDialog({ modalData: { handleConfirm: handleResetProduct, productId: data?.productId, actionType: "remove" }, modal: "DELETE_DISCREPANCY" }))

                  // setSelectedProductId(data?.productId);
                  // dispatch(openCustomModalDialog({
                  //   modalData: {
                  //     handleSubmit: handleResetProduct,
                  //     title: "Remove Product",
                  //     description: <>
                  //       This will remove the product from the driver kit.
                  //       <br />
                  //       Are you sure you want to remove?
                  //     </>,
                  //     submitBtnLabel: "Remove",
                  //     productId: data?.productId,
                  //     actionType: "remove"
                  //   },
                  //   modal: 'CONFIRMATION_MODAL'
                  // }))
                }}
                disabled={resettingProduct === data?.productId}
              >
                <DeleteIcon width='18px' height='18px' />
              </IconButton>
            </>
            }
            {/* <IconButton
                  color='error'
                  size='small'
                  variant='primary'
                  style={{ width: "32px", height: "32px" }}
                  onClick={(e) => {
                    e.stopPropagation();
                    dispatch(openCustomModalDialog({ modalData: { deleteHandler, deleteId: data.distributionId }, modal: 'DELETE_MODAL' }))
                  }}
                >
                  <DeleteIcon width='18px' height='18px' />
                </IconButton> */}
          </Box>
        },
      },)
    }

    return columns;
  }, [details?.status])

  const handleResetProduct = async (modalData) => {
    try {
      const { productId, actionType } = modalData || {}
      setResettingProduct(productId);
      dispatch(setModalLoader(true))

      const response = await dispatch(resetDriverKitProduct({ distributionId, driverId, regionId, subRegionId, productId })).unwrap();
      const { data, success, message } = response || {};

      if (success) {
        showToastMsg(message || "Product reset successfully", "success");

        if (actionType === "reset") {
          setDetails(prev => {
            const updatedProducts = prev?.products?.map(product => {
              if (product?.productId === productId) {
                return { ...product, scannedQty: 0, discrepancyQty: 0, discrepancy: 0, discrepancyId: "", discrepancyType: "", assignedQty: product.expectedQty }
              }
              return product
            })
            return { ...prev, products: updatedProducts }
          });

          handleDriverProducts();
          // debounceHandleDriverProducts();
        } else if (actionType === "remove") {
          setDetails(prev => {
            const updatedProducts = prev?.products?.filter(product => product?.productId !== productId)
            return { ...prev, products: updatedProducts }
          });

          handleDriverProducts();
        }

        // debounceHandleDriverProducts();
      }
    }
    catch (error) {
      showToastMsg(error, "error");
    } finally {
      setResettingProduct("");
      dispatch(closeCustomModalDialog())
      dispatch(setModalLoader(false))
    }
  };

  const handleKeepExtraProduct = async (modalData) => {
    try {
      const { productId } = modalData || {};
      setResettingProduct(productId);
      dispatch(setModalLoader(true))

      const response = await dispatch(keepExtraDriverKitProduct({ distributionId, driverId, regionId, subRegionId, productId })).unwrap();
      const { data, success, message } = response || {};

      if (success) {
        showToastMsg(message || "Product reset successfully", "success");

        // setDetails(prev => {
        //   const updatedProducts = prev?.products?.map(product => {
        //     if (product?.productId === productId) {
        //       return { ...product, discrepancyQty: 0, discrepancy: 0, discrepancyId: "", discrepancyType: "", assignedQty: product.scannedQty, expectedQty: product.scannedQty, isNewProduct: false }
        //     }
        //     return product
        //   })
        //   return { ...prev, products: updatedProducts }
        // });

        handleDriverProducts();
      }
    }
    catch (error) {
      showToastMsg(error, "error");
    } finally {
      setResettingProduct("");
      dispatch(closeCustomModalDialog())
      dispatch(setModalLoader(false))
    }
  };

  const handleGetConfigs = async () => {
    try {
      const response = await dispatch(getDistributionConfig()).unwrap()
      const data = response?.data;
      if (data) {
        setDistributionConfigs(data || {});
      }
    } catch (error) {
    }
  }

  const handleDriverProducts = async () => {
    try {
      setIsLoading(true);
      const response = await dispatch(getDriverKitProducts({ distributionId, driverId, regionId, subRegionId, ...customFilters, limit: 2000 })).unwrap();
      const { data } = response || {};
      if (data) {
        setDetails(data || {});
        statusRef.current = data?.status || ""
      }
    }
    catch (error) {
      showToastMsg(error, "error");
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (e) => {
    const { value, name } = e.target;

    setCustomFilters(prev => ({ ...prev, [name]: value }));
  };

  const handleDebounceSearch = useCallback(debounce((event) => {
    setCustomFilters(prev => ({ ...prev, search: event.target.value }));
  }, 1000), []);

  const handleClearFilters = () => {
    setCustomFilters({});
    setSearchTerm("");
  };

  const debounceHandleDriverProducts = useCallback(debounce(handleDriverProducts, 5000), []);

  const handleScanProduct = useCallback(async (value) => {
    if (isScanning || !value) return;
    setIsScanning(true);
    try {
      // Socket
      if (!isConnected) {
        setIsScanning(false);
        return showToastMsg("Something went wrong, Please try again", "error");
      }
      if (value) {
        const payload = { distributionId, driverId, productBatchId: value, regionId, subRegionId, userId: user?._id }
        socket.emit('scanProduct', payload, (response) => {
          setIsScanning(false);

          const { success, message, product, summary } = response || {};
          const { productId } = product || {};

          if (success) {
            showToastMsg(message || "Product scanned successfully", "success");
            setDetails(prev => {
              const products = prev?.products || [];
              const productIndex = products.findIndex(p => p.productId === productId);

              if (productIndex > -1) {
                const updatedProduct = { ...products[productIndex], ...product };
                const otherProducts = products.filter((_, index) => index !== productIndex);
                return {
                  ...prev,
                  products: [updatedProduct, ...otherProducts],
                  summary: { ...prev.summary, scanned: summary?.scanned }
                };

              } else if (productIndex === -1) {
                return {
                  ...prev,
                  products: [product, ...(prev.products || [])],
                  summary: { ...prev.summary, scanned: summary?.scanned }
                };
              }

              return prev;
            });
          } else {
            showToastMsg(message || "Something went wrong", "error");
          }
        });
      }
    } catch (error) {
      showToastMsg(error, "error");
      setIsScanning(false);
    } finally {
      // setIsScanning(false) // Use this only for api flow
    }
  }, [isConnected, distributionId, driverId, regionId, subRegionId, user?._id, isScanning]);

  const handleResetKit = async () => {
    try {
      dispatch(setModalLoader(true));

      const response = await dispatch(resetDriverKit({ distributionId, driverId })).unwrap();
      dispatch(closeCustomModalDialog());
      if (response?.success) {
        showToastMsg(response?.message || "Kit reset successfully", "success")
        handleDriverProducts();
      }
    }
    catch (error) {
      showToastMsg(error, "error")
    } finally {
      dispatch(setModalLoader(false));
    }
  }

  const handleUpdateStatus = async (status, skipRefresh = false) => {
    if (isUpdatingStatus) return;

    try {
      setIsUpdatingStatus(status)
      // return;
      const response = await dispatch(holdDriverKit({ distributionId, driverId, regionId, subRegionId, status })).unwrap();

      if (response?.success) {
        showToastMsg(response?.message || "Success", "success")
        // update status immediately after update
        setDetails(prev => ({ ...prev, status: status === "hold" ? "On Hold" : status }));
        statusRef.current = status || "";
        // Update details if not skipping refresh
        if (!skipRefresh) {
          handleDriverProducts();
        }
      }
    }
    catch (error) {
      showToastMsg(error, "error")
    } finally {
      setIsUpdatingStatus(null)
    }
  }

  //   useEffect(() => {
  //     handleGetConfigs();
  //   }, [])

  useEffect(() => {
    if (distributionId && driverId && regionId && subRegionId) {
      handleDriverProducts()
    }
  }, [distributionId, driverId, regionId, subRegionId, customFilters])

  useEffect(() => {
    return () => {
      const shouldHold = !skipHoldOnUnmountRef.current;
      const status = statusRef.current;

      if (shouldHold && status && !["dispatched", "On Hold", "paused"].includes(status?.toLowerCase())) {
        sessionStorage.setItem("hold_kit_pending", "true");
        handleUpdateStatus("hold")
      }
    }
  }, []);

  const canDispatch = useMemo(() => {
    if (!details?.products?.length) return false;

    const assignedQty = details?.products?.reduce((acc, product) => acc + product?.assignedQty, 0);
    const scannedQty = details?.products?.reduce((acc, product) => acc + Math.min(product?.scannedQty, product?.assignedQty), 0);
    return scannedQty >= assignedQty || details.status?.toLowerCase() === "paused";
  }, [details.products, details.status])

  return (
    <CommonLayout>
      {/* bar code scanner */}
      {/* {details?.status && !["on hold", "dispatched", "paused"].includes(details?.status?.toLowerCase()) && <BarcodeScanArea handleScanProduct={handleScanProduct} />} */}

      <Box p={4} className="cus-contents">
        <Stack
          direction={{ xs: "column", sm: "row" }}
          alignItems={{ sm: "center" }}
          justifyContent="space-between"
          gap={2}
          mb={2}
        >
          <Box display="flex" alignItems="center" color="text.main" lineHeight={1}>
            <IconButton onClick={async () => {
              const status = statusRef.current;

              if (status && !["dispatched", "On Hold", "paused"].includes(status?.toLowerCase())) {
                await handleUpdateStatus("hold", true)
              }
              skipHoldOnUnmountRef.current = true;
              navigate(-1)
            }}>
              <AngleRightIcon fontSize="14px" />
            </IconButton>
            <Typography variant="h6" fontWeight="semibold" ml={0.75}>
              Driver List
            </Typography>
          </Box>
        </Stack>

        {/* Top section */}
        <Card sx={{ p: { xs: 2, md: 3 }, mb: 3, borderRadius: 2.5, boxShadow: "none" }}>
          <Grid container alignItems="center" rowSpacing={2} columnSpacing={2}>
            {/* Top details */}
            <Grid item xs={12} sm={6}>
              <Typography variant='h4' fontWeight="semibold">Products List</Typography>
              <List sx={{ display: "flex", gap: 3, mt: 0.5 }}>
                <ListItem sx={{ width: "auto" }}>
                  <Typography variant="body2" fontWeight="medium" color={"#656575"}>Driver:</Typography>
                  <Typography variant="body2" fontWeight="semibold" ml={1}>{details?.driverName}</Typography>
                </ListItem>
                <ListItem sx={{ width: "auto" }}>
                  <Typography variant="body2" fontWeight="medium" color={"#656575"}>Distribution:</Typography>
                  <Typography variant="body2" fontWeight="semibold" ml={1}>{details?.distributionType}</Typography>
                </ListItem>
                <ListItem sx={{ width: "auto" }}>
                  <Typography variant="body2" fontWeight="medium" color={"#656575"}>Kit ID:</Typography>
                  <Typography variant="body2" fontWeight="semibold" ml={1}>{details?.templateId}</Typography>
                </ListItem>
              </List>
            </Grid>

            {/* Action buttons */}
            {details?.status && details?.status?.toLowerCase() !== "dispatched" && <Grid item xs={12} sm={6}>
              <Stack direction='row' gap={1} alignItems='center' justifyContent="end">
                <div className="d-flex align-item-top gap-2">
                  <>
                    {/* On-Hold and Resume */}
                    {
                      ["on hold", "paused"].includes(details?.status?.toLowerCase()) ?
                        <MDButton
                          variant="contained"
                          color="primary"
                          sx={{ minWidth: "160px", background: "#2E2E3A !important", color: "#ffffff !important", boxShadow: "none !important" }}
                          disabled={isUpdatingStatus || isLoading}
                          onClick={() => handleUpdateStatus(details?.status?.toLowerCase() === "paused" ? "pending" : "resumed")}
                        >
                          {["resumed", "pending"].includes(isUpdatingStatus) ? <CircularProgress size={22} color="light" /> : details?.status?.toLowerCase() === "paused" ? "Start Scanning" : "Resume"}
                        </MDButton>
                        : <>
                          <MDButton
                            variant="contained"
                            color="primary"
                            sx={{ minWidth: "160px", background: "#2E2E3A !important", color: "#ffffff !important", boxShadow: "none !important" }}
                            disabled={isUpdatingStatus || isLoading}
                            onClick={() => handleUpdateStatus("paused")}
                          >
                            {isUpdatingStatus === "paused" ? <CircularProgress size={22} color="light" /> : "Stop Scanning"}
                          </MDButton>
                          <MDButton
                            variant="contained"
                            color="primary"
                            sx={{ minWidth: "160px", background: "#2E2E3A !important", color: "#ffffff !important", boxShadow: "none !important" }}
                            disabled={isUpdatingStatus || isLoading}
                            onClick={() => handleUpdateStatus("hold")}
                          >
                            {isUpdatingStatus === "hold" ? <CircularProgress size={22} color="light" /> : "On Hold"}
                          </MDButton>
                        </>
                    }
                    {details?.status !== "On Hold" &&
                      <>
                        {/* Dispatch button */}
                        {canDispatch &&
                          <MDButton
                            sx={{ minWidth: "160px", boxShadow: "none !important" }}
                            variant="contained"
                            color="primary"
                            disabled={isUpdatingStatus || isLoading}
                            onClick={() => {
                              if (isUpdatingStatus || isLoading) return;
                              skipHoldOnUnmountRef.current = true;
                              navigate(
                                `/hyperwolf/driver-kit-verification/kit-report/${distributionId}/${driverId}?subRegionId=${subRegionId}&regionId=${regionId}`,
                                { state: { skipHoldOnUnmount: true } }
                              )
                            }}
                          >
                            Dispatch
                          </MDButton>
                        }

                        {/* Reset button */}
                        <MDButton
                          sx={{ minWidth: "160px", boxShadow: "none !important" }}
                          variant="contained"
                          color="primary"
                          disabled={isUpdatingStatus || isLoading}
                          onClick={() => {
                            if (isUpdatingStatus || isLoading) return;
                            dispatch(openCustomModalDialog({
                              modalData: {
                                handleSubmit: handleResetKit, title: "Reset Confirmation", description: "Are you sure you want to reset this kit?", submitBtnLabel: "Reset"
                              },
                              modal: 'CONFIRMATION_MODAL'
                            }))
                          }}
                        >
                          Reset
                        </MDButton>
                      </>
                    }
                  </>
                </div>
              </Stack>
            </Grid>}
          </Grid>
        </Card>

        <Card sx={{ p: { xs: 2, md: 3 }, borderRadius: 2.5, boxShadow: "none" }}>
          {/* Quantity data */}
          <Stack direction='row' gap={3} mb={3} alignItems='start' justifyContent="space-between">
            <Card sx={{ p: { xs: 2 }, borderRadius: 2.5, boxShadow: "none", height: "100%", border: "1px solid #F2F3F7", flex: "1" }}>
              <div className="d-flex align-item-center gap-2 mb-3">
                <div style={{ width: "30px", height: "30px", display: "flex", alignItems: "center", justifyContent: "center", borderRadius: "8px", background: "#F4792D", boxShadow: "0 12.522px 10.017px 0 rgba(255, 119, 22, 0.05), 0 6.65px 5.32px 0 rgba(255, 119, 22, 0.04), 0 2.767px 2.214px 0 rgba(255, 119, 22, 0.03)" }}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
                    <g clipPath="url(#clip0_17533_120975)">
                      <path d="M9 0.5621C4.34343 0.5621 0.5625 4.34302 0.5625 8.9996C0.5625 13.6562 4.34343 17.4371 9 17.4371C13.6566 17.4371 17.4375 13.6562 17.4375 8.9996C17.4375 7.70535 17.1446 6.4789 16.6229 5.38232L15.7852 6.22005C15.627 6.37912 15.4113 6.46882 15.187 6.46886H14.954C15.2856 7.24654 15.4693 8.1017 15.4693 8.9996C15.4693 12.5689 12.5693 15.4689 9 15.4689C5.43074 15.4689 2.5307 12.5689 2.5307 8.9996C2.5307 5.43034 5.43074 2.53026 9 2.53026C9.89807 2.53026 10.7535 2.71388 11.5313 3.04554V2.81262C11.5313 2.58829 11.621 2.37261 11.7801 2.21443L12.6172 1.37673C11.5207 0.854952 10.2942 0.5621 9 0.5621ZM14.3361 0.5621C14.2641 0.564246 14.1957 0.593893 14.1449 0.644984L12.1767 2.61318C12.1504 2.63931 12.1296 2.67041 12.1153 2.70463C12.1011 2.73885 12.0938 2.77553 12.0938 2.81259V4.66546L8.37982 8.37938C8.03937 8.71984 8.03937 9.28092 8.37982 9.62138C8.72028 9.96183 9.27973 9.96018 9.62018 9.61924L13.3336 5.90587H15.187C15.224 5.90586 15.2607 5.89856 15.2949 5.88433C15.3291 5.8701 15.3602 5.84926 15.3864 5.82298L17.3546 3.85475C17.3937 3.81535 17.4203 3.76526 17.431 3.71075C17.4418 3.65625 17.4361 3.59978 17.4148 3.54849C17.3934 3.4972 17.3574 3.45336 17.3112 3.42252C17.265 3.39169 17.2107 3.37522 17.1552 3.37519H15.8643L16.0884 3.1511C16.4288 2.81064 16.4305 2.25118 16.0901 1.91073C15.9199 1.7405 15.6942 1.65529 15.4688 1.65529C15.2434 1.65529 15.0183 1.74054 14.8481 1.91073L14.6245 2.1343V0.841765C14.6244 0.804315 14.6167 0.767266 14.6021 0.732799C14.5875 0.698332 14.5661 0.667153 14.5392 0.641059C14.5123 0.614964 14.4805 0.59449 14.4457 0.58085C14.4108 0.56721 14.3735 0.561153 14.3361 0.5621ZM9 4.49849C6.51805 4.49849 4.4989 6.51765 4.4989 8.9996C4.4989 11.4815 6.51805 13.4985 9 13.4985C11.4819 13.4985 13.4989 11.4815 13.4989 8.9996C13.4989 8.28354 13.3272 7.60828 13.0287 7.00665L11.4879 8.54748C11.5145 8.69443 11.5307 8.8451 11.5307 8.9996C11.5307 10.3942 10.3946 11.5303 9 11.5303C7.60536 11.5303 6.4693 10.3942 6.4693 8.9996C6.4693 7.60495 7.60536 6.46886 9 6.46886C9.15449 6.46886 9.30514 6.48503 9.45209 6.51172L10.9935 4.97033C10.3918 4.67123 9.71651 4.49849 9 4.49849Z" fill="white" />
                    </g>
                    <defs>
                      <clipPath id="clip0_17533_120975">
                        <rect width="18" height="18" fill="white" />
                      </clipPath>
                    </defs>
                  </svg>
                </div>
                <Typography variant="h6" fontWeight="600" color="#2E2E3A">Expected Quantity</Typography>
              </div>
              <div className="d-grid grid-columns-three">
                <div className="d-flex align-item-top flex-column gap-1">
                  <Typography variant='body2' color="#656575">Products Count</Typography>
                  <Typography variant='h6' color="#2E2E3A" fontWeight="600">{details?.summary?.expected?.productCount?.toLocaleString()}</Typography>
                </div>
                {/* {distributionConfigs.isRetailer && <div className="d-flex align-item-top flex-column gap-1">
                  <Typography variant='body2' color="#656575">Retail Value</Typography>
                  <Typography variant='h6' color="#2E2E3A" fontWeight="600">${details?.summary?.expected?.retailValue?.toLocaleString()}</Typography>
                </div>} */}
                <div className="d-flex align-item-top flex-column gap-1">
                  <Typography variant='body2' color="#656575">Kit Value</Typography>
                  <Typography variant='h6' color="#2E2E3A" fontWeight="600">${details?.summary?.expected?.wholesaleValue?.toLocaleString()}</Typography>
                </div>
              </div>
            </Card>
            <Card sx={{ p: { xs: 2 }, borderRadius: 2.5, boxShadow: "none", height: "100%", border: "1px solid #F2F3F7", flex: "1" }}>
              <div className="d-flex align-item-center gap-2 mb-3">
                <div style={{ width: "30px", height: "30px", display: "flex", alignItems: "center", justifyContent: "center", borderRadius: "8px", background: "#A162F7", boxShadow: "0 12.522px 10.017px 0 rgba(154, 22, 255, 0.05), 0 6.65px 5.32px 0 rgba(189, 22, 255, 0.04), 0 2.767px 2.214px 0 rgba(197, 22, 255, 0.03)" }}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
                    <path d="M16.3125 3.375V6.1875C16.3125 6.33668 16.2532 6.47976 16.1477 6.58525C16.0423 6.69074 15.8992 6.75 15.75 6.75C15.6008 6.75 15.4577 6.69074 15.3523 6.58525C15.2468 6.47976 15.1875 6.33668 15.1875 6.1875V3.9375H12.9375C12.7883 3.9375 12.6452 3.87824 12.5398 3.77275C12.4343 3.66726 12.375 3.52418 12.375 3.375C12.375 3.22582 12.4343 3.08274 12.5398 2.97725C12.6452 2.87176 12.7883 2.8125 12.9375 2.8125H15.75C15.8992 2.8125 16.0423 2.87176 16.1477 2.97725C16.2532 3.08274 16.3125 3.22582 16.3125 3.375ZM5.0625 14.0625H2.8125V11.8125C2.8125 11.6633 2.75324 11.5202 2.64775 11.4148C2.54226 11.3093 2.39918 11.25 2.25 11.25C2.10082 11.25 1.95774 11.3093 1.85225 11.4148C1.74676 11.5202 1.6875 11.6633 1.6875 11.8125V14.625C1.6875 14.7742 1.74676 14.9173 1.85225 15.0227C1.95774 15.1282 2.10082 15.1875 2.25 15.1875H5.0625C5.21168 15.1875 5.35476 15.1282 5.46025 15.0227C5.56574 14.9173 5.625 14.7742 5.625 14.625C5.625 14.4758 5.56574 14.3327 5.46025 14.2273C5.35476 14.1218 5.21168 14.0625 5.0625 14.0625ZM15.75 11.25C15.6008 11.25 15.4577 11.3093 15.3523 11.4148C15.2468 11.5202 15.1875 11.6633 15.1875 11.8125V14.0625H12.9375C12.7883 14.0625 12.6452 14.1218 12.5398 14.2273C12.4343 14.3327 12.375 14.4758 12.375 14.625C12.375 14.7742 12.4343 14.9173 12.5398 15.0227C12.6452 15.1282 12.7883 15.1875 12.9375 15.1875H15.75C15.8992 15.1875 16.0423 15.1282 16.1477 15.0227C16.2532 14.9173 16.3125 14.7742 16.3125 14.625V11.8125C16.3125 11.6633 16.2532 11.5202 16.1477 11.4148C16.0423 11.3093 15.8992 11.25 15.75 11.25ZM2.25 6.75C2.39918 6.75 2.54226 6.69074 2.64775 6.58525C2.75324 6.47976 2.8125 6.33668 2.8125 6.1875V3.9375H5.0625C5.21168 3.9375 5.35476 3.87824 5.46025 3.77275C5.56574 3.66726 5.625 3.52418 5.625 3.375C5.625 3.22582 5.56574 3.08274 5.46025 2.97725C5.35476 2.87176 5.21168 2.8125 5.0625 2.8125H2.25C2.10082 2.8125 1.95774 2.87176 1.85225 2.97725C1.74676 3.08274 1.6875 3.22582 1.6875 3.375V6.1875C1.6875 6.33668 1.74676 6.47976 1.85225 6.58525C1.95774 6.69074 2.10082 6.75 2.25 6.75ZM12.51 5.85773L9.28125 4.01133C9.19624 3.96274 9.10002 3.93718 9.00211 3.93718C8.90419 3.93718 8.80798 3.96274 8.72297 4.01133L5.49 5.85773C5.44604 5.88206 5.4094 5.91771 5.38388 5.96099C5.35837 6.00427 5.34491 6.05359 5.34491 6.10383C5.34491 6.15407 5.35837 6.20339 5.38388 6.24667C5.4094 6.28995 5.44604 6.3256 5.49 6.34992L9 8.35242L12.51 6.34641C12.5531 6.32183 12.589 6.28628 12.6139 6.24336C12.6388 6.20045 12.652 6.1517 12.652 6.10207C12.652 6.05244 12.6388 6.00369 12.6139 5.96078C12.589 5.91787 12.5531 5.88231 12.51 5.85773ZM4.5 7.5607V11.25C4.49911 11.3496 4.52468 11.4476 4.57409 11.5341C4.6235 11.6206 4.69499 11.6924 4.78125 11.7422L8.01562 13.5893C8.05841 13.614 8.10695 13.627 8.15635 13.627C8.20576 13.627 8.25429 13.6139 8.29705 13.5892C8.33982 13.5645 8.37532 13.5289 8.39998 13.4861C8.42463 13.4433 8.43757 13.3947 8.4375 13.3453V9.32625L4.92188 7.31672C4.87909 7.29202 4.83055 7.27902 4.78115 7.27904C4.73174 7.27906 4.68321 7.29209 4.64045 7.31682C4.59768 7.34156 4.56218 7.37712 4.53752 7.41993C4.51287 7.46275 4.49993 7.5113 4.5 7.5607ZM13.5 11.25V7.5607C13.5001 7.5113 13.4871 7.46275 13.4625 7.41993C13.4378 7.37712 13.4023 7.34156 13.3596 7.31682C13.3168 7.29209 13.2683 7.27906 13.2189 7.27904C13.1694 7.27902 13.1209 7.29202 13.0781 7.31672L9.5625 9.32625V13.3418C9.56243 13.3912 9.57537 13.4398 9.60003 13.4826C9.62468 13.5254 9.66018 13.5609 9.70294 13.5857C9.74571 13.6104 9.79424 13.6234 9.84365 13.6235C9.89305 13.6235 9.94159 13.6105 9.98438 13.5858L13.2188 11.7422C13.305 11.6924 13.3765 11.6206 13.4259 11.5341C13.4753 11.4476 13.5009 11.3496 13.5 11.25Z" fill="white" />
                  </svg>
                </div>
                <Typography variant="h6" fontWeight="600" color="#2E2E3A">Scanned Quantity</Typography>
              </div>
              <div className="d-grid grid-columns-three">
                <div className="d-flex align-item-top flex-column gap-1">
                  <Typography variant='body2' color="#656575">Products Count</Typography>
                  <Typography variant='h6' color="#2E2E3A" fontWeight="600">{details?.summary?.scanned?.productCount?.toLocaleString()}</Typography>
                </div>
                {/* {distributionConfigs.isRetailer && <div className="d-flex align-item-top flex-column gap-1">
                  <Typography variant='body2' color="#656575">Retail Value</Typography>
                  <Typography variant='h6' color="#2E2E3A" fontWeight="600">${details?.summary?.scanned?.retailValue?.toLocaleString()}</Typography>
                </div>} */}
                <div className="d-flex align-item-top flex-column gap-1">
                  <Typography variant='body2' color="#656575">Kit Value</Typography>
                  <Typography variant='h6' color="#2E2E3A" fontWeight="600">${details?.summary?.scanned?.wholesaleValue?.toLocaleString()}</Typography>
                </div>
              </div>
            </Card>
          </Stack>

          {/* Notes */}
          {(details?.notes || details?.photos?.length > 0) && <Card sx={{ p: { xs: 2 }, mb: 3, borderRadius: 2.5, boxShadow: "none", height: "100%", border: "1px solid #F2F3F7", flex: "1" }}>
            <Grid container alignItems="start" spacing={2}>
              <Grid item xs={12}>
                <Typography variant="h6" fontWeight="600" color="#2E2E3A">Discrepancy Information</Typography>
              </Grid>

              <Grid item xs={12}>
                <Stack direction="row" flexWrap="wrap" mb={2} flexDirection="column">
                  <Typography variant='body2' color="#656575" mb={1}>Image</Typography>
                  {details?.photos?.length > 0 && <div style={{ display: "flex", flexWrap: "wrap", gap: "10px" }}>
                    {
                      details?.photos?.map((image, index) => (
                        <div style={{ position: "relative" }} key={index}>
                          <img
                            key={image}
                            src={image}
                            alt="Uploaded"
                            style={{
                              borderRadius: "20px",
                              width: "130px",
                              height: "130px",
                              objectFit: "cover",
                              boxShadow: "0 0 1px rgb(0 0 0 / 20%)",
                            }}
                          />
                        </div>
                      ))
                    }
                  </div>}
                </Stack>

                <Box>
                  <Typography variant='body2' color="#656575">Note</Typography>
                  {details?.notes && <Typography variant='h6' color="#2E2E3A" fontWeight="600">{details?.notes}</Typography>}
                </Box>

              </Grid>
            </Grid>
          </Card>}

          {details?.status && !["on hold", "dispatched", "paused"].includes(details?.status?.toLowerCase()) && !isOpen && <SocketMobileScanner handleScanProduct={handleScanProduct} />}

          <Grid container alignItems="center" rowSpacing={2} columnSpacing={2} mb={3}>
            {/* Filters */}
            <Grid item xs={12}>
              <Box className='cus-filter-sec filter-gray-bg' mb={1}>
                <Grid className='filter-box' container alignItems='center' justifyContent='flex-start' spacing={1.5}>
                  <Grid item xs={12} lg={3} xxxl={2}>
                    <BoxFilter
                      value={customFilters?.boxId}
                      handleChange={handleChange}
                      placeholder={"Box Type"}
                      selectAllLabel={"All Boxes"}
                    />
                  </Grid>
                  <Grid item xs={12} lg={3} xxxl={2}>
                    <BrandFilter
                      value={customFilters?.brandId}
                      handleChange={handleChange}
                      placeholder={"Brands"}
                      selectAllLabel={"All"}
                    />
                  </Grid>
                  <Grid item xs={12} lg={3} xxxl={2} className='filter-search'>
                    <TextField
                      className="custom-search-field"
                      placeholder="Search"
                      value={searchTerm || ""}
                      onChange={e => {
                        const { value } = e.target;
                        setSearchTerm(value);
                        handleDebounceSearch(e);
                      }}
                      InputProps={{
                        startAdornment: <InputAdornment position="start">
                          <IconButton size='small'>
                            <SearchIcon width='18px' height='18px' />
                          </IconButton>
                        </InputAdornment>
                      }}
                      style={{ width: "100%" }}
                    />
                  </Grid>
                  <Grid item xs={12} lg={3} xxxl={2}>
                    <MDButton
                      sx={{ minWidth: "120px", fontWeight: "normal" }}
                      type="button"
                      variant="outlined"
                      color="dark"
                      onClick={handleClearFilters}
                      disabled={!Object.values(customFilters)?.filter(Boolean).some((v) => v)}
                    >
                      Clear All
                    </MDButton>
                  </Grid>
                </Grid>
              </Box>
            </Grid>

            {/* Products listing */}
            <Grid item xs={12} className="table-nodata-size table-border-gray">
              <div
                className="ag-theme-quartz"
                style={{ height: "55vh", width: "100%" }}
              >
                {
                  isLoading ?
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "360px", textAlign: "center" }}>
                      <CircularProgress size={34} />
                    </div>
                    : <AgGridReact
                      className="ag-grid--table"
                      pagination={false}
                      rowData={details.products || []}
                      noRowsOverlayComponent={NoDataFound}
                      overlayLoadingTemplate={'<span class="table-loader"></span>'}
                      columnDefs={columnDefs}
                      rowModelType={"clientSide"}
                      headerHeight={36}
                      rowHeight={56}
                      rowSelection="multiple"
                      suppressRowClickSelection={true}
                      defaultColDef={{ ...defaultColDef }}
                      getRowId={getRowId}
                    />
                }
              </div>
            </Grid>
          </Grid >
        </Card >

      </Box >
    </CommonLayout >
  )
}

export default DriverKitDetails
